/**
 * StudentActivityRecorder — l'enregistreur d'une activité orale : le moteur de la voix
 * (`engine/voice/useVoiceRecorder`), la réécoute (`useConversationVoicePlayer`, celui de la
 * messagerie) et la feuille du design system (`<VoiceRecorderSheet>`).
 *
 * Monté le temps que la feuille est ouverte, comme l'ancien `AudioVideoActivityStep` dans « Mon
 * travail ». « Envoyer » confie l'enregistrement à la file de la copie (S3 au vrai type de base, puis
 * `/logs`) et referme ; la croix abandonne. En live, la pause du tuteur met l'enregistrement en pause
 * (comme l'ancien) et la session audio tient compte de l'appel (`liveCallActive`). L'ouverture et la
 * fermeture sont dites au moteur : l'attribut vocal du live suit, remis à faux à toute sortie.
 */
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { Linking, Platform, Vibration } from "react-native"

import { VoiceRecorderSheet } from "../../../../../../design-system/student"
import { type CopyFile } from "../../../../../../engine/activity/copy/copyOutbox"
import { useVoiceRecorder } from "../../../../../../engine/voice/useVoiceRecorder"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { useConversationVoicePlayer } from "../Messaging/useConversationVoicePlayer"
import { RECORDER_VIBRATION_MS, recorderVibrates } from "./recorderHaptics"

export type StudentActivityRecorderProps = {
    onClose: () => void
    onSend: (file: CopyFile) => void
    /** L'enregistreur s'ouvre et se ferme : le moteur le dit au live. */
    onOpened: () => void
    onClosed: () => void
    /** La pause du tuteur (en live). */
    paused: boolean
    /** Un appel tient la session audio (le live). */
    liveCallActive: boolean
}

const DRAFT_ID = "activity-voice-draft"
const TITLE = "Mon travail"
const SEND_LABEL = "Envoyer mon travail"

export function StudentActivityRecorder(props: StudentActivityRecorderProps) {
    const { onClose, onSend, onOpened, onClosed, paused, liveCallActive } = props
    const recorder = useVoiceRecorder({ liveCallActive })
    const preview = useConversationVoicePlayer(null)

    useEffect(() => {
        onOpened()
        return () => onClosed()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (paused && recorder.state === "recording") recorder.pause()
    }, [paused, recorder])

    // La vibration de l'ancien enregistreur, à la pause, à la reprise et à la fin.
    const previousState = useRef<typeof recorder.state | null>(null)
    useEffect(() => {
        if (recorderVibrates(previousState.current, recorder.state)) Vibration.vibrate(RECORDER_VIBRATION_MS)
        previousState.current = recorder.state
    }, [recorder.state])

    const draft = useMemo<UIConversationMessage | null>(
        () =>
            recorder.uri != null
                ? { id: DRAFT_ID, author: "self", attachment: { kind: "voice", uri: recorder.uri, durationLabel: "" } }
                : null,
        [recorder.uri]
    )

    const close = useCallback(() => {
        preview.stop()
        void recorder.reset()
        onClose()
    }, [preview, recorder, onClose])

    const send = useCallback(() => {
        if (recorder.uri == null || recorder.mimeType == null || recorder.extension == null) return
        preview.stop()
        onSend({
            uri: recorder.uri,
            mimeType: recorder.mimeType,
            extension: recorder.extension,
            durationMs: recorder.durationMs,
        })
        void recorder.reset()
        onClose()
    }, [preview, recorder, onSend, onClose])

    const settings = recorder.state === "denied" && !recorder.canAskAgain && Platform.OS !== "web"

    return (
        <VoiceRecorderSheet
            visible
            title={TITLE}
            onClose={close}
            phase={recorder.state}
            durationMs={recorder.durationMs}
            runningSince={recorder.runningSince}
            maxDurationMs={recorder.maxDurationMs}
            notice={recorder.notice}
            onStart={() => void recorder.start()}
            onPause={recorder.pause}
            onResume={recorder.resume}
            onStop={() => void recorder.stop()}
            stopping={recorder.stopping}
            playback={
                draft != null
                    ? {
                          playing: preview.messageId === DRAFT_ID && preview.playing,
                          progress: preview.messageId === DRAFT_ID ? preview.progress : 0,
                          onToggle: () => preview.toggle(draft),
                      }
                    : undefined
            }
            onRedo={() => {
                preview.stop()
                void recorder.start()
            }}
            onSend={send}
            sendLabel={SEND_LABEL}
            onOpenSettings={settings ? () => void Linking.openSettings() : undefined}
        />
    )
}
